import { motion } from "framer-motion";
import { HandHeart, Flame, Palette, Briefcase, Dumbbell, Globe, Compass, Users, Mic, Home, Footprints, Sparkles } from "lucide-react";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}>
    {children}
  </motion.div>
);

const NOW = [
  { icon: Mic, title: "Reflection Nights", body: "Real stories, guided reflection, honest conversation." },
  { icon: Home, title: "The Outpoured House", body: "Multi-day immersive practice in the real world." },
  { icon: Footprints, title: "Go & Do Challenges", body: "Daily, hands-on practice right where you are." },
  { icon: Users, title: "Gatherings & Cohorts", body: "In-person communities that practice together." },
];

const FUTURE = [
  { icon: HandHeart, title: "Prayer, Healing & Deliverance" },
  { icon: Flame, title: "Prophetic" },
  { icon: Palette, title: "Creative Arts & Worship" },
  { icon: Briefcase, title: "Marketplace & Business" },
  { icon: Dumbbell, title: "Fitness & Wellness" },
  { icon: Globe, title: "Missions & Evangelism" },
  { icon: Compass, title: "Leadership Development" },
  { icon: Sparkles, title: "Outreach — prison, foster care, community, local & global" },
];

export const Pathways = ({ dark = false }) => {
  const bg = dark ? "bg-[#2A2825] text-[#F4EFE6]" : "bg-[#EAE3D9]/40 text-[#2A2825]";
  const sub = dark ? "text-[#C9BEB0]" : "text-[#5E4B3C]";
  const accent = dark ? "text-[#E8B486]" : "text-[#A9724E]";
  const card = dark ? "bg-[#33302C] border-[#4a4540] hover:border-[#E8B486]/60" : "bg-[#F4EFE6] border-[#D8CEBE] hover:border-[#A9724E]/60";
  const chip = dark ? "border-[#4a4540] text-[#D9CFC2] hover:border-[#E8B486]/60" : "border-[#D1C9BE] text-[#5E4B3C] hover:border-[#A9724E]";
  return (
    <section data-testid="pathways" className={`py-24 md:py-32 border-b border-[#D1C9BE] grain relative ${bg}`}>
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <Reveal><p className={`tracking-overline text-xs uppercase mb-4 ${accent}`}>One vision · many pathways</p></Reveal>
        <Reveal delay={0.06}>
          <h2 className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6 max-w-3xl">Different ways of putting faith <span className={`italic ${accent}`}>into practice.</span></h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className={`font-body leading-relaxed text-lg max-w-2xl mb-14 ${sub}`}>Outpoured Ministries is a spiritual formation hub — a training ground where believers are gathered, equipped, encouraged, inspired, and sent out to live their calling. Everything we do fits under that one vision. These are the doorways.</p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-14">
          {NOW.map((p, i) => (
            <Reveal key={p.title} delay={0.05 * i}>
              <div data-testid={`pathway-now-${i}`} className={`h-full border p-6 transition-colors ${card}`}>
                <p.icon className={`w-6 h-6 mb-5 ${accent}`} strokeWidth={1.25} />
                <h3 className="font-serif-display text-xl md:text-2xl font-light leading-snug mb-2">{p.title}</h3>
                <p className={`text-sm font-body leading-relaxed ${sub}`}>{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className={`tracking-overline text-[10px] uppercase mb-5 ${accent}`}>Pathways we're building toward</p>
        </Reveal>
        <div className="flex flex-wrap gap-2.5" data-testid="pathways-future">
          {FUTURE.map((p, i) => (
            <Reveal key={p.title} delay={0.03 * i}>
              <span className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-body transition-colors ${chip}`}>
                <p.icon className={`w-4 h-4 ${accent}`} strokeWidth={1.5} />{p.title}
              </span>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className={`mt-12 font-serif-display italic text-2xl md:text-3xl leading-snug ${accent}`}>The goal isn't to know more about Jesus. It's to become more like Him in the way we actually live.</p>
        </Reveal>
      </div>
    </section>
  );
};
